/**
 * Optimizaciones para renderizado eficiente de muchas rutas en Google Maps
 */

/**
 * Simplifica un path de coordenadas usando el algoritmo Ramer-Douglas-Peucker
 * Reduce significativamente el número de puntos manteniendo la forma general
 * @param path Array de coordenadas [lat, lng]
 * @param epsilon Tolerancia de simplificación (mayor = más simplificado)
 * @returns Array simplificado de coordenadas
 */
export function simplifyPath(
    path: Array<{ lat: number; lng: number }>,
    epsilon: number = 0.0001
): Array<{ lat: number; lng: number }> {
    if (path.length <= 2){ 
        return path;
    }

    // Encontrar el punto con mayor distancia perpendicular
    let maxDistance = 0;
    let maxIndex = 0;

    const line = {
        start: path[0],
        end: path[path.length - 1]
    };

    for (let i = 1; i < path.length - 1; i++) {
        const distance = perpendicularDistance(path[i], line.start, line.end);
        if (distance > maxDistance) {
            maxDistance = distance;
            maxIndex = i;
        }
    }

    // Si la distancia máxima es mayor que epsilon, dividir recursivamente
    if (maxDistance > epsilon) {
        const left = simplifyPath(path.slice(0, maxIndex + 1), epsilon);
        const right = simplifyPath(path.slice(maxIndex), epsilon);
        return [...left.slice(0, -1), ...right];
    } else {
        return [line.start, line.end];
    }
}

/**
 * Calcula la distancia perpendicular de un punto a una línea
 */
function perpendicularDistance(
    point: { lat: number; lng: number },
    lineStart: { lat: number; lng: number },
    lineEnd: { lat: number; lng: number }
): number {
    const { lat: px, lng: py } = point;
    const { lat: x1, lng: y1 } = lineStart;
    const { lat: x2, lng: y2 } = lineEnd;

    if (x1 === x2 && y1 === y2) {
        return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    }

    const t = Math.max(
        0,
        Math.min(
            1,
            ((px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)) / ((x2 - x1) ** 2 + (y2 - y1) ** 2)
        )
    );

    const closestX = x1 + t * (x2 - x1);
    const closestY = y1 + t * (y2 - y1);

    return Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);
}

/**
 * Decima un array de puntos tomando cada N puntos
 * Útil para zoom out - mostrar menos detalles
 * @param points Array de puntos
 * @param interval Cada cuántos puntos tomar
 */
export function decimatePoints<T>(points: T[], interval: number = 2): T[] {
    if (interval <= 1){ return points;}
    return points.filter((_, i) => i % interval === 0 || i === points.length - 1);
}

/**
 * Determina el nivel de decimación basado en el zoom del mapa
 * Mayor zoom = menos decimación (más detalles)
 * Menor zoom = más decimación (menos puntos)
 */
export function getDecimationFactor(zoom: number): number {
    // Zoom 1-3: mostrar cada 10 puntos
    if (zoom <= 3){ return 10;}
    // Zoom 4-6: mostrar cada 8 puntos
    if (zoom <= 6){ return 8;}
    // Zoom 7-10: mostrar cada 5 puntos
    if (zoom <= 10){ return 5;}
    // Zoom 11-14: mostrar cada 3 puntos
    if (zoom <= 14){ return 3;}
    // Zoom 15+: mostrar todos los puntos
    return 1;
}

/**
 * Agrupa puntos cercanos en clusters
 * Útil para mostrar una vista simplificada cuando hay muchísimos puntos
 */
export function clusterPoints(
    points: Array<{ lat: number; lng: number; data?: any }>,
    gridSize: number = 0.01
): Array<{
    lat: number;
    lng: number;
    count: number;
    points: Array<{ lat: number; lng: number; data?: any }>;
}> {
    const clusters = new Map<string, Array<{ lat: number; lng: number; data?: any }>>();

    for (const point of points) {
        const gridX = Math.floor(point.lat / gridSize);
        const gridY = Math.floor(point.lng / gridSize);
        const key = `${gridX},${gridY}`;

        if (!clusters.has(key)) {
            clusters.set(key, []);
        }
        clusters.get(key)!.push(point);
    }

    return Array.from(clusters.values()).map(pointsGroup => {
        const avgLat = pointsGroup.reduce((sum, p) => sum + p.lat, 0) / pointsGroup.length;
        const avgLng = pointsGroup.reduce((sum, p) => sum + p.lng, 0) / pointsGroup.length;

        return {
            lat: avgLat,
            lng: avgLng,
            count: pointsGroup.length,
            points: pointsGroup
        };
    });
}

/**
 * Filtra rutas visibles basado en los bounds del mapa
 * Solo retorna rutas que intersectan con el área visible
 */
export function filterVisibleRoutes(
    routes: any[],
    mapBounds: google.maps.LatLngBounds
): any[] {
    if (!routes) { return []; }

    return routes.filter(route => {
        if (!route.paradas || route.paradas.length === 0){ return false; }

        // Verificar si al menos una parada está dentro de los bounds
        return route.paradas.some((parada: any) => {
            const point = new google.maps.LatLng(
                parada.coordenadas[1],
                parada.coordenadas[0]
            );
            return mapBounds.contains(point);
        });
    });
}

/**
 * Obtiene puntos clave de una ruta (depósito inicio, depósito fin, puntos intermedios)
 */
export function getKeyStops(paradas: any[], maxPoints: number = 5): any[] {
    if (paradas.length <= maxPoints){ return paradas;}

    const keyStops = [
        paradas[0], // Primer stop (depósito salida)
        paradas[paradas.length - 1] // Último stop (depósito llegada)
    ];

    // Agregar puntos intermedios distribuidos uniformemente
    const interval = Math.floor((paradas.length - 2) / (maxPoints - 2));
    for (let i = 1; i < paradas.length - 1; i += interval) {
        if (keyStops.length < maxPoints) {
            keyStops.push(paradas[i]);
        }
    }

    return keyStops;
}

/**
 * Calcula el bounding box de un conjunto de paradas
 */
export function calculateBoundingBox(paradas: any[]): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    for (const parada of paradas) {
        bounds.extend(
            new google.maps.LatLng(parada.coordenadas[1], parada.coordenadas[0])
        );
    }
    return bounds;
}

/**
 * Calcula memoria estimada para renderizar rutas
 * Útil para warnings cuando hay demasiada data
 */
export function estimateMemoryUsage(routes: any[]): { 
    totalPoints: number; 
    estimatedMB: number; 
    recommendedVirtualization: boolean 
} {
    let totalPoints = 0;

    for (const route of routes) {
        if (route.paradas) {
            totalPoints += route.paradas.length;
        }
    }

    // Cada polyline point ≈ 200 bytes + overhead de React
    const estimatedBytes = totalPoints * 200 + routes.length * 1000;
    const estimatedMB = estimatedBytes / (1024 * 1024);

    return {
        totalPoints,
        estimatedMB,
        recommendedVirtualization: estimatedMB > 10 // Más de 10MB es sospechoso
    };
}

/**
 * Detecta si el navegador está bajo presión de memoria
 */
export function isMemoryPressure(): boolean {
    if (!(performance as any).memory) {
        return false;
    }

    const { usedJSHeapSize, jsHeapSizeLimit } = (performance as any).memory;
    const usage = usedJSHeapSize / jsHeapSizeLimit;
    return usage > 0.85; // Más del 85% de memoria usada
}

/**
 * Pool de reuso de objetos para reducir garbage collection
 */
export class PointPool {
    private pool: Array<{ lat: number; lng: number }> = [];

    acquire(lat: number, lng: number): { lat: number; lng: number } {
        let point = this.pool.pop();
        if (!point) {
            point = { lat, lng };
        } else {
            point.lat = lat;
            point.lng = lng;
        }
        return point;
    }

    release(point: { lat: number; lng: number }): void {
        this.pool.push(point);
    }

    clear(): void {
        this.pool = [];
    }
}

/**
 * Limita la tasa de ejecución de una función (throttle)
 */
export function throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
): (...args: any[]) => void {
    let inThrottle = false;
    return function(this: any, ...args: any[]) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

/**
 * Debounce - ejecuta función solo después de que dejan de llegar eventos
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: any[]) => void {
    let timeout: any;
    return function(this: any, ...args: any[]) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}



// WEBPACK FOOTER //
// ./src/components/planificacion-multiday/mapOptimizations.ts